import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Dumbbell, Eye, EyeOff, Loader2 } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { fr } from "../../locales/fr"

const LOGIN_HERO = `${process.env.PUBLIC_URL || ""}/images/login-hero.jpg`

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()
  const from = location.state?.from?.pathname || "/"

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      const msg = err?.response?.data?.message
      setError(typeof msg === "string" ? msg : fr.auth.login.errorInvalid)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-1/2 lg:block">
        <img
          src={LOGIN_HERO}
          alt={fr.auth.login.heroAlt}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-primary/20" />
        <div className="relative flex h-full flex-col justify-between p-10">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary shadow-lg">
              <Dumbbell className="h-7 w-7 text-primary-foreground" aria-hidden />
            </div>
            <span className="text-2xl font-bold text-white">{fr.brand}</span>
          </div>
          <p className="max-w-md text-2xl font-semibold leading-snug text-white drop-shadow-sm">
            {fr.auth.login.heroTagline}
          </p>
        </div>
      </div>

      <div className="relative flex w-full flex-1 flex-col lg:w-1/2">
        <div className="relative h-44 shrink-0 overflow-hidden lg:hidden">
          <img
            src={LOGIN_HERO}
            alt=""
            aria-hidden
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
          <div className="absolute bottom-4 left-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <Dumbbell className="h-6 w-6 text-primary-foreground" aria-hidden />
            </div>
            <span className="text-xl font-bold text-white drop-shadow">{fr.brand}</span>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center p-4 sm:p-8">
          <div className="w-full max-w-md">
            <div className="mb-8 hidden justify-center lg:flex">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
                  <Dumbbell className="h-7 w-7 text-primary-foreground" aria-hidden />
                </div>
                <span className="text-2xl font-bold text-foreground">{fr.brand}</span>
              </div>
            </div>

            <Card className="border-border/60 shadow-xl">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{fr.auth.login.title}</CardTitle>
                <CardDescription>{fr.auth.login.subtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error ? (
                    <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground">
                      {fr.auth.login.email}
                    </label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-foreground">
                      {fr.auth.login.password}
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder={fr.auth.login.passwordPlaceholder}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {fr.auth.login.submitting}
                      </>
                    ) : (
                      fr.auth.login.submit
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
